import React from 'react';
import { ScrollView, RefreshControl, StyleSheet } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useThemeColors } from '@/hooks/residents/useThemeColors';
import { REDESIGN_SPACING } from '@/constants/residents/homeConstants';
import { useResourcesScreen } from '@/hooks/residents/useResourcesScreen';
import { ResourcesScreenSkeleton } from './ResourcesScreenSkeleton';


import {
  PageHeader,
  SectionHeader,
} from '@/components/residents/redesign';
import { ResourceGlassCard } from '@/components/residents/redesign/resources';
import { ScreenBackLink } from '@/components/residents/mobile/ScreenBackLink';
import { MobileWifiSupportModal } from '@/components/residents/resources/mobile/MobileWifiSupportModal';

export const MobileResourcesScreen: React.FC = () => {
  const { colors } = useThemeColors();
  const insets = useSafeAreaInsets();
  const {
    resources,
    wifiInfo,
    isLoading,
    refreshing,
    subtitle,
    hasHandbookUrl,
    handleRefresh,
    handleResourcePress,
    handleContactOfficePress,
    handleResidentHandbookPress,
  } = useResourcesScreen();

  const [showSupportModal, setShowSupportModal] = React.useState(false);

  if (isLoading && resources.length === 0) {
    return <ResourcesScreenSkeleton />;
  }

  return (
    <>
      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={[
          styles.scrollContent,
          { paddingBottom: 40 + insets.bottom },
        ]}
        showsVerticalScrollIndicator={false}
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={handleRefresh}
            tintColor={colors.text.primary}
          />
        }
      >
        <ScreenBackLink style={styles.backLink} />
        <PageHeader title="Resources" subtitle={subtitle} />

        <SectionHeader title="My Resources" />

        <ResourceGlassCard
          title="Contact the office"
          description="Get in touch with your property management"
          iconName="chatbubble-outline"
          iconBg={colors.accent.blueBgLight}
          onPress={handleContactOfficePress}
        />

        {hasHandbookUrl && (
          <ResourceGlassCard
            title="Resident handbook"
            description="View FAQs and community guidelines"
            iconName="book-outline"
            iconBg={colors.accent.purpleLight}
            onPress={handleResidentHandbookPress}
          />
        )}

        {resources.map((resource) => (
          <ResourceGlassCard
            key={resource.id}
            resource={resource}
            iconBg={colors.accent.orangeLight}
            onPress={handleResourcePress}
          />
        ))}
      </ScrollView>

      {/* WiFi Support Modal (kept for when WiFi feature is re-enabled) */}
      <MobileWifiSupportModal
        visible={showSupportModal}
        wifiInfo={wifiInfo}
        onClose={() => setShowSupportModal(false)}
      />
    </>
  );
};

const styles = StyleSheet.create({
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    paddingHorizontal: REDESIGN_SPACING.horizontalPadding,
    gap: 12,
  },
  backLink: {
    marginBottom: -4,
  },
});
